/**
 * Map and navigation helpers (docs/directory-plan.md section 7).
 * Pure: no Leaflet, no i18n, no browser APIs.
 */
import type { AreaConfig, ListingLocation } from '../types';
import { approximateAddress } from './listingDisplay';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Where a listing goes on the map. `approximate` = faded "area" pin instead of an exact pin. */
export interface MapPoint extends LatLng {
  approximate: boolean;
}

/** Grid size (degrees) for hidden locations: about 110 m north-south, 95 m east-west here. */
export const APPROX_GRID_DEG = 0.001;

/** The approximate point is never more than this far from the real spot (grid half-diagonal ≈ 73 m). */
export const APPROX_MAX_OFFSET_M = 80;

export function hasCoords(loc: Partial<LatLng> | undefined | null): loc is LatLng {
  return (
    !!loc &&
    typeof loc.lat === 'number' &&
    typeof loc.lng === 'number' &&
    Number.isFinite(loc.lat) &&
    Number.isFinite(loc.lng) &&
    !(loc.lat === 0 && loc.lng === 0)
  );
}

/**
 * Snaps a point to a fixed grid, so the pin for a hidden location never
 * sits on the house itself. Same input always gives the same point.
 */
export function approximatePoint(point: LatLng): LatLng {
  const snap = (v: number) => Math.round(Math.round(v / APPROX_GRID_DEG) * APPROX_GRID_DEG * 1e6) / 1e6;
  return { lat: snap(point.lat), lng: snap(point.lng) };
}

/** The point to draw (exact pin, or approximate point when the owner hid the location). */
export function mapPointOf(location: ListingLocation | undefined): MapPoint | null {
  if (!location || !hasCoords(location)) return null;
  if (location.showExactLocation === false) {
    return { ...approximatePoint(location), approximate: true };
  }
  return { lat: location.lat, lng: location.lng, approximate: false };
}

/** Straight-line distance in meters (haversine). */
export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Distance for display: under 1 km in meters rounded to 10 ("350 m"),
 * otherwise km with one decimal ("1.2 km"). The component translates the unit.
 */
export function formatDistance(meters: number): { unit: 'm' | 'km'; value: string } {
  if (meters < 995) return { unit: 'm', value: String(Math.max(10, Math.round(meters / 10) * 10)) };
  return { unit: 'km', value: (meters / 1000).toFixed(1) };
}

// ---------- Navigate (Google Maps default, Waze option) ----------

/** Either an exact point or a text search (street only, for hidden locations). */
export type NavTarget = { kind: 'point'; lat: number; lng: number } | { kind: 'query'; query: string };

/**
 * Where Navigate goes. Exact location by default; when the owner hid the
 * exact location, the street only (no house number) plus the city (decided by
 * Yoel, Oct 1 2026). Null when there's nothing to navigate to.
 */
export function navTargetOf(
  location: ListingLocation | undefined,
  area: Pick<AreaConfig, 'cityEn' | 'cityHe'> | undefined,
  isHebrew: boolean
): NavTarget | null {
  if (!location) return null;
  const hidden = location.showExactLocation === false;
  if (!hidden && hasCoords(location)) return { kind: 'point', lat: location.lat, lng: location.lng };

  // Hebrew first in Hebrew mode; either way fall back to the other language.
  const address = isHebrew
    ? location.addressHe || location.addressEn
    : location.addressEn || location.addressHe;
  const street = hidden ? approximateAddress(address) : (address ?? '').trim();
  if (!street) return null;
  const usesHebrew = /[֐-׿]/.test(street);
  const city = area ? (usesHebrew ? area.cityHe : area.cityEn) : '';
  return { kind: 'query', query: city ? `${street}, ${city}` : street };
}

export function googleMapsUrl(target: NavTarget): string {
  const destination = target.kind === 'point' ? `${target.lat},${target.lng}` : target.query;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

export function wazeUrl(target: NavTarget): string {
  if (target.kind === 'point') return `https://waze.com/ul?ll=${target.lat},${target.lng}&navigate=yes`;
  return `https://waze.com/ul?q=${encodeURIComponent(target.query)}&navigate=yes`;
}
